"use client";

import {
  BadgeCheck,
  Building2,
  MapPin,
  ShieldCheck,
  UserCheck,
  X,
} from "lucide-react";

type VerificationDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  identityVerified: boolean;
  registrationVerified: boolean;
  addressVerified: boolean;
};

export default function VerificationDetailsModal({
  isOpen,
  onClose,
  companyName,
  identityVerified,
  registrationVerified,
  addressVerified,
}: VerificationDetailsModalProps) {
  if (!isOpen) {
    return null;
  }

  const criteria = [
    {
      key: "identity",
      title: "Identidade verificada",
      description:
        "Responsável e documento de identificação conferidos.",
      verified: identityVerified,
      icon: UserCheck,
    },
    {
      key: "registration",
      title: "Cadastro verificado",
      description:
        "CPF ou CNPJ e dados cadastrais conferidos.",
      verified: registrationVerified,
      icon: Building2,
    },
    {
      key: "address",
      title: "Endereço verificado",
      description:
        "Endereço comercial ou informado foi conferido.",
      verified: addressVerified,
      icon: MapPin,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/55 p-0 backdrop-blur-sm sm:items-center sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verification-details-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-[2rem] bg-white shadow-2xl sm:max-w-lg sm:rounded-[2rem]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-6">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
              <ShieldCheck size={25} />
            </div>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-600">
                Fornecedor.net
              </p>

              <h2
                id="verification-details-title"
                className="mt-1 text-xl font-black text-slate-950"
              >
                Empresa verificada
              </h2>

              <p className="mt-1 truncate text-sm font-semibold text-slate-500">
                {companyName}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Fechar detalhes da verificação"
          >
            <X size={21} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm leading-6 text-slate-600">
            O Fornecedor.net analisou os dados indicados
            abaixo antes de conceder o selo
            <strong className="font-bold text-slate-800">
              {" "}Empresa Verificada
            </strong>.
          </p>

          <div className="mt-6 space-y-3">
            {criteria.map((criterion) => {
              const Icon = criterion.icon;

              return (
                <div
                  key={criterion.key}
                  className={[
                    "flex items-start gap-4 rounded-2xl border p-4",
                    criterion.verified
                      ? "border-emerald-200 bg-emerald-50/70"
                      : "border-slate-200 bg-slate-50",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                      criterion.verified
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-200 text-slate-500",
                    ].join(" ")}
                  >
                    <Icon size={20} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-slate-900">
                        {criterion.title}
                      </p>

                      {criterion.verified && (
                        <BadgeCheck
                          size={17}
                          className="shrink-0 text-emerald-600"
                        />
                      )}
                    </div>

                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {criterion.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-amber-800">
              Importante
            </p>

            <p className="mt-2 text-xs leading-5 text-amber-900/80">
              A verificação confirma somente os dados
              indicados acima. O selo não representa
              garantia de pagamento, entrega, qualidade
              dos produtos ou serviços, nem do cumprimento
              das obrigações comerciais pela empresa.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
